import { useState, useMemo, useEffect } from "react";
import { useLocation, Link, useParams, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Menu as MenuIcon,
  CircleDashed,
  BookOpen,
  Ellipsis,
  RefreshCw,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { DarkModeToggle } from "./dark-mode-toggle";
import { twMerge } from "tailwind-merge";
import { AccountSheet } from "./account-sheet";
import LogoIcon from "@/assets/myth-icon.svg?react";
import LogoWordmark from "@/assets/myth-horizontal.svg?react";
import LogoStacked from "@/assets/myth-stacked.svg?react";
import { dsDisabled, network } from "@/config";
import { DiscordButton, GithubButton, TwitterButton } from "./footer";
import { cx } from "class-variance-authority";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { findListingByLstName, preventDefault } from "@/lib/utils";
import { SettingsDialog } from "./settings-dialog";
import { changeNetwork } from "@/lib/network";
import { useSortedListings } from "@/hooks/use-sorted-listings";
import { useDSSortStore } from "@/store/ds-listing-sort";
import { getHighlightClasses } from "@/lib/get-tokens-highlights";
import {
  HoverCard,
  HoverCardTrigger,
  HoverCardContent,
} from "@/components/ui/hover-card";
import { ListingCard } from "@/pages/dualstake/components/listing-card";
import { useFarmsAndAPRs } from "@/hooks/use-farms-and-aprs";
import { HoverCardPortal } from "@radix-ui/react-hover-card";
import { useIsFetching } from "@tanstack/react-query";
import { queryClient } from "@/lib/query-client";

const navItems1 = {
  dualSTAKE: {
    regex: /dualSTAKE\/?$/,
    path: "/dualSTAKE",
  },
  Bench: {
    regex: /bench\/?[0-9]*$/,
    path: "/bench",
  },
};

const navItems2 = {
  // Analytics: {
  //   regex: /analytics(\/)?$/,
  //   path: "/analytics",
  // },
  Docs: {
    regex: /unused-just-dont-match-anything/,
    path: "https://docs.myth.finance",
  },
};

if (dsDisabled) {
  // @ts-ignore
  delete navItems1.dualSTAKE;
  // @ts-ignore
  delete navItems2.Analytics;
}

type Fn = () => void;

function Logo({ className, onClick }: { onClick?: Fn; className?: string }) {
  return (
    <Link to="/" onClick={onClick} className={className}>
      <div className="flex">
        <Button
          variant="link"
          className="text-lg font-bold -my-2 text-foreground/90"
        >
          <LogoIcon
            className="lg:hidden block"
            style={{ width: 40, height: 40 }}
          />
          <LogoWordmark
            className="hidden lg:block"
            style={{ width: "192px", height: "45px" }}
          />
        </Button>
      </div>
    </Link>
  );
}

export function MainNav() {
  const location = useLocation();
  const params = useParams();
  const listings = useSortedListings(true, true);

  const [stateLeft, stateRight] = useMemo(() => {
    const one = Object.entries(navItems1).map(([name, { path, regex }]) => {
      return {
        name,
        path,
        active: regex.test(location.pathname),
      };
    });
    const two = Object.entries(navItems2).map(([name, { path, regex }]) => {
      return {
        name,
        path,
        active: regex.test(location.pathname),
      };
    });
    if (listings?.length && params.token) {
      const listing = findListingByLstName(listings, params.token);
      if (listing) {
        const { lstName } = listing;
        const regex = new RegExp(`dualSTAKE\/${lstName}`, "i");
        one.splice(1, 0, {
          name: lstName,
          path: `dualSTAKE/${lstName}`,
          active: regex.test(location.pathname),
        });
      }
    }
    return [one, two];
  }, [location, listings, params]);
  const { data: farmsAndAprs } = useFarmsAndAPRs(
    listings ? [...listings.values()] : []
  );
  const navigate = useNavigate();

  const [hoverOpen, setHoverOpen] = useState(false);

  useEffect(() => {
    setHoverOpen(false);
  }, [location.pathname]);

  const isFetching = useIsFetching();

  return (
    <div className="hidden gap-2 md:flex items-center flex-grow">
      <Logo />
      <NetworkSwitcher className={`mr-2 -ml-1`} />
      {isFetching ? (
        <CircleDashed
          className={`animate-spin transition-all text-foreground h-4 w-4`}
        />
      ) : (
        <RefreshCw
          role="button"
          tabIndex={0}
          onKeyDown={(event: any) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              queryClient.invalidateQueries();
            }
          }}
          onClick={() => queryClient.invalidateQueries()}
          className="text-muted-foreground transition-all h-4 w-4 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        />
      )}

      {stateLeft.map(({ name, path, active }, idx) =>
        path !== "/dualSTAKE" ? (
          <Link to={path} key={idx}>
            <Button variant={active ? "secondary" : "ghost"}>{name}</Button>
          </Link>
        ) : (
          <HoverCard
            openDelay={200}
            closeDelay={200}
            open={!farmsAndAprs ? false : hoverOpen}
          >
            <HoverCardTrigger
              onMouseEnter={() => setHoverOpen(true)}
              onMouseLeave={() => setHoverOpen(false)}
              asChild
            >
              <Link to={path} key={idx}>
                <Button
                  variant={hoverOpen ? undefined : "ghost"}
                  className={hoverOpen ? "rounded-b-[0]" : ""}
                >
                  {name}
                </Button>
              </Link>
            </HoverCardTrigger>
            <HoverCardPortal>
              <HoverCardContent
                align="start"
                onMouseEnter={() => setHoverOpen(true)}
                onMouseLeave={() => setHoverOpen(false)}
                onClick={preventDefault}
                className="w-auto -mt-[6px] border-primary rounded-xl rounded-tl-[0] bg-background dark:bg-card"
              >
                {!farmsAndAprs ? null : (
                  <div className="grid grid-cols-2 xl:grid-cols-3 gap-4 pt-4">
                    {listings.slice(0, 9).map((listing, i) => (
                      <ListingCard
                        small={true}
                        farmAndApr={farmsAndAprs?.get(listing.appId)!}
                        listing={listing}
                        onClick={() => setHoverOpen(false)}
                        key={`li-${i}`}
                        className={i > 5 ? "hidden xl:block" : ""}
                      />
                    ))}
                    <div className="relative col-span-2 xl:col-span-3 w-full justify-self-center flex justify-center items-center">
                      <Button
                        className="absolute left-0 bottom-0"
                        variant="ghost"
                        size="sm"
                        onClick={
                          () =>
                            (window.location.href = `https://docs.myth.finance/dualSTAKE/overview`) /* TODO dialog with overview */
                        }
                      >
                        <BookOpen /> How does it work?
                      </Button>
                      <Button
                        variant="outline"
                        // size="lg"
                        onClick={() => navigate("/dualSTAKE")}
                        className="py-6 hover:border-primary"
                      >
                        <Ellipsis />
                        Show all ({listings.length})
                      </Button>
                    </div>
                  </div>
                )}
              </HoverCardContent>
            </HoverCardPortal>
          </HoverCard>
        )
      )}
      <Spacer />
      {stateRight.map(({ name, path, active }, idx) => (
        <Link to={path} key={`fnt-${idx}`}>
          <Button variant={active ? "secondary" : "ghost"}>{name}</Button>
        </Link>
      ))}
    </div>
  );
}

export function NetworkSwitcher({ className }: { className?: string }) {
  const prettyNetwork = network.toUpperCase();
  if (network === "mainnet") return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger className={cx("cursor-pointer", className)}>
        <Badge variant="destructive">{prettyNetwork}</Badge>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56">
        <DropdownMenuLabel>Change Network</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup
          value={prettyNetwork}
          onValueChange={changeNetwork}
        >
          <DropdownMenuRadioItem value="Mainnet">Mainnet</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="Testnet">Testnet</DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="Fnet">Fnet</DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function MobileNav() {
  const [open, setOpen] = useState(false);

  const location = useLocation();

  const { ownFirst, sortValue } = useDSSortStore();
  const listings = useSortedListings(true, true);

  const state = useMemo(() => {
    const onClick = () => setOpen(false);
    const left = Object.entries(navItems1).map(([name, { path, regex }]) => ({
      name,
      path,
      className: "",
      active: regex.test(location.pathname),
      onClick,
    }));
    const right = Object.entries(navItems2).map(([name, { path, regex }]) => ({
      name,
      path,
      className: "",
      active: regex.test(location.pathname),
      onClick,
    }));
    if (listings) {
      const dualSTAKEs = listings.slice(0, 5).map(({ lstName, asaId }) => ({
        name: lstName,
        path: `dualSTAKE/${lstName}`,
        active: new RegExp(`dualSTAKE\/${lstName}`, "i").test(
          location.pathname
        ),
        className: `ml-4 ${getHighlightClasses(asaId, "text")}`,
        onClick,
      }));
      if (dualSTAKEs.length < listings.length) {
        const more = listings.length - dualSTAKEs.length;
        dualSTAKEs.push({
          name: `Show ${more} More`,
          path: `dualSTAKE`,
          active: false,
          className: `ml-4 text-muted-foreground`,
          onClick,
        });
      }
      left.splice(1, 0, ...dualSTAKEs);
    }

    return [...left, ...right];
  }, [location, listings, ownFirst, sortValue]);

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        {/* This button will trigger open the mobile sheet menu */}
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon" className="md:hidden">
            <MenuIcon />
          </Button>
        </SheetTrigger>

        <SheetContent side="left" className="flex w-64 overflow-y-auto">
          <div className="flex flex-col gap-2 flex-grow items-start">
            <LogoStacked
              className="mt-3 mb-6 self-center text-foreground/90"
              style={{ width: "90%", minHeight: "50px" }}
              onClick={() => {
                setOpen(false);
              }}
            />
            {state.map(({ name, path, active, onClick, className }, idx) => (
              <Link to={path} key={idx}>
                <Button
                  variant={active ? "secondary" : "ghost"}
                  onClick={onClick}
                  className={twMerge("px-7", className)}
                >
                  {name}
                </Button>
              </Link>
            ))}
            <Spacer />
            <div className="flex self-stretch items-end justify-between">
              <div className="flex gap-2">
                <GithubButton />
                <TwitterButton />
                <DiscordButton />
              </div>
              <div className="flex flex-col gap-2">
                <SettingsDialog variant="outline" />
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>
      <Spacer className="md:hidden ml-8" />
      <DarkModeToggle className="inline-flex invisible md:hidden" />
      <SettingsDialog className="inline-flex invisible ml-2 md:hidden" />

      <div className="md:hidden relative flex">
        <NetworkSwitcher className="absolute top-[50%] -left-[100%] -translate-y-[50%]" />
        <Logo className="md:hidden ml-2" />
      </div>

      <Spacer className="md:hidden" />
      <DarkModeToggle className="inline-flex invisible md:visible ml-2 mr-2 md:mr-4 md:mr-0" />
      <SettingsDialog className="inline-flex ml-0 mr-4 md:mr-0 md:ml-2" />
    </>
  );
}

function Spacer({ className }: { className?: string }) {
  return <div className={twMerge("flex-grow", className)}></div>;
}

export default function SiteHeader() {
  return (
    <header className="w-full bg-card/25 backdrop-blur-md">
      <div className="flex h-14 items-center px-4">
        <MainNav />
        <MobileNav />
        <AccountSheet />
      </div>
    </header>
  );
}
